import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink } from "react-router-dom";
import { PAGE } from "components/common/CONSTANTS";
import { useUploadReceiptFile } from "./useUploadReceiptFile";
import { MESSAGES } from "./constants";

export const UploadReceiptFile = () => {
  const {
    acceptedFiles,
    fileError,
    showUploadingDialog,
    resultDialog,
    dropzoneStyle,
    isDragReject,
    fileRejections,
    getRootProps,
    getInputProps,
    removeFile,
    handleUploadFile,
    handleResultDialogClose,
    downloadTemplate,
  } = useUploadReceiptFile();

  let dialogTitle = "";
  if (resultDialog.open) {
    dialogTitle = resultDialog.status === "success" ? MESSAGES.UPLOAD_SUCCESS_TITLE : MESSAGES.UPLOAD_FAILED_TITLE;
  }

  let dialogContent = null;
  if (resultDialog.status === "success") {
    dialogContent = <DialogContentText>{MESSAGES.UPLOAD_SUCCESS_MSG}</DialogContentText>;
  } else if (resultDialog.status === "partial-success") {
    dialogContent = (
      <DialogContentText>
        {resultDialog.failedRecords} of {resultDialog.totalRecords} records
        could not be processed. Please{" "}
        <Link href={resultDialog.errorFileUrl ?? "#"} target="_blank" rel="noopener noreferrer">
          {MESSAGES.DOWNLOAD_ERROR_LINK}
        </Link>
        .
      </DialogContentText>
    );
  } else if (resultDialog.status === "error") {
    dialogContent = (
      <DialogContentText>
        All {resultDialog.totalRecords} records failed to process. Please{" "}
        <Link href={resultDialog.errorFileUrl ?? "#"} target="_blank" rel="noopener noreferrer">
          {MESSAGES.DOWNLOAD_ERROR_LINK}
        </Link>
        .
      </DialogContentText>
    );
  } else if (resultDialog.status === "API_ERROR") {
    dialogContent = <DialogContentText>{MESSAGES.UPLOAD_API_ERROR_MSG}</DialogContentText>;
  }

  const acceptedFileItems = acceptedFiles.map((file) => (
    <ListItem key={file.path} sx={{ pl: 0 }} dense disablePadding>
      <ListItemIcon sx={{ minWidth: "auto", pr: 1 }}>
        <DescriptionOutlinedIcon />
      </ListItemIcon>
      {file.path}
      <Button component={Link} onClick={removeFile(file)} color="error">
        Remove
      </Button>
    </ListItem>
  ));

  const rejectedFileItems = fileRejections.map(({ file }) => (
    <ListItem key={file.path} sx={{ pl: 0 }} dense disablePadding>
      <ListItemIcon sx={{ minWidth: "auto", pr: 1, color: "red" }}>
        <DescriptionOutlinedIcon />
      </ListItemIcon>
      {file.path}
    </ListItem>
  ));

  return (
    <div className="mainPageStyling">
      <div style={{ position: "relative" }}>
        <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
          {PAGE.UPLOAD_PO_RECEIPT_FILE.label}
        </Typography>

        <div className="downloadLink" style={{ top: "0" }}>
          <FileDownloadOutlinedIcon
            onClick={downloadTemplate}
            style={{
              height: "18px",
              color: "#108810",
              cursor: "pointer",
              position: "relative",
              top: "5px",
            }}
          />
          <span>
            <Typography>
              <RouterLink
                style={{
                  color: "#108810",
                  cursor: "pointer",
                  fontSize: "11px",
                }}
                onClick={downloadTemplate}
                to="#"
              >
                Download Template
              </RouterLink>
            </Typography>
          </span>
        </div>
      </div>

      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        component="form"
        onSubmit={handleUploadFile}
        sx={{ "& .MuiFormHelperText-root": { ml: 0 } }}
      >
        <Grid container spacing={1} item>
          <Grid item xs={12}>
            <div
              {...getRootProps({ style: dropzoneStyle })}
              className="uploadPageStyling"
            >
              <input data-testid="dropzone" {...getInputProps()} />
              <FileUploadOutlinedIcon fontSize="large" />
              <span>{MESSAGES.DROPZONE_HINT}</span>
              <Typography variant="body2">
                {MESSAGES.DROPZONE_FILE_HINT}
              </Typography>
              {isDragReject && (
                <Typography color="error.main">
                  {MESSAGES.DROPZONE_REJECT}
                </Typography>
              )}
            </div>
            <Typography variant="caption" color="error.main">
              {acceptedFileItems.length ? "" : fileError}
            </Typography>
            {acceptedFileItems.length > 0 && (
              <aside className="asideStyle">
                <List sx={{ pt: 0.5 }} dense>
                  {acceptedFileItems}
                  {rejectedFileItems}
                </List>
              </aside>
            )}
          </Grid>
        </Grid>
        <div className="commonButtons">
          <Grid container item sx={{ textAlign: "left", marginTop: "8px" }}>
            <Grid item xs={4} sm={2}></Grid>
            <Grid item xs={4} sm={2}>
              <Button
                variant="contained"
                type="submit"
                disabled={!acceptedFiles[0]?.path}
              >
                Upload
              </Button>
              <Grid sx={{ paddingBottom: 17 }}></Grid>
            </Grid>
          </Grid>
        </div>
      </Grid>

      {showUploadingDialog && (
        <Dialog open maxWidth="sm" fullWidth>
          <DialogTitle>{MESSAGES.UPLOAD_DIALOG_TITLE}</DialogTitle>
          <DialogContent>
            <LinearProgress />
            <Typography>{MESSAGES.UPLOAD_DIALOG_WARNING}</Typography>
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={resultDialog.open}
        maxWidth="sm"
        fullWidth
        onClose={handleResultDialogClose}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {dialogTitle}
          <IconButton
            aria-label="close"
            onClick={handleResultDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>{dialogContent}</DialogContent>
        <DialogActions sx={{ pl: 3, pb: 3 }}>
          <Button variant="contained" onClick={handleResultDialogClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
